'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Search, ChevronDown, ChevronUp, Car } from 'lucide-react';
import VehicleBrandLogo from '@/components/VehicleBrandLogo';
import { CAR_BRAND_LOGOS } from '@/lib/brand-logos';

interface CountryAllMakesDirectoryProps {
  countrySlug: string;
  countryName: string;
  popularMakes: string[];
}

const REGIONAL_GROUPS: Record<string, string[]> = {
  Japanese: [
    'Toyota', 'Honda', 'Nissan', 'Mitsubishi', 'Mazda', 'Subaru', 'Suzuki', 'Isuzu', 'Daihatsu', 'Lexus', 'Infiniti', 'Acura', 'Mitsuoka'
  ],
  German: [
    'Mercedes-Benz', 'BMW', 'Volkswagen', 'Audi', 'Porsche', 'Opel', 'Smart', 'Alpina', 'Maybach'
  ],
  Korean: [
    'Hyundai', 'Kia', 'Genesis', 'SsangYong', 'Daewoo'
  ],
  American: [
    'Ford', 'Chevrolet', 'GMC', 'Jeep', 'Dodge', 'Ram', 'Chrysler', 'Cadillac', 'Buick', 'Lincoln', 'Tesla', 'Hummer', 'Pontiac', 'Saturn'
  ],
  European: [
    'Peugeot', 'Renault', 'Citroën', 'Fiat', 'Alfa Romeo', 'Volvo', 'Škoda', 'SEAT', 'Cupra', 'Dacia', 'Land Rover', 'Jaguar', 'Mini', 'Aston Martin', 'Bentley', 'Rolls-Royce', 'Maserati', 'Ferrari', 'Lamborghini'
  ],
  'Chinese & Emerging': [
    'BYD', 'Chery', 'Geely', 'Haval', 'Great Wall', 'Changan', 'Dongfeng', 'FAW', 'BAIC', 'JAC', 'Jetour', 'GAC', 'Hongqi', 'Maxus', 'Foton', 'MG', 'Tata', 'Mahindra'
  ]
};

export default function CountryAllMakesDirectory({
  countrySlug,
  countryName,
  popularMakes,
}: CountryAllMakesDirectoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<string>('All');
  const [isExpanded, setIsExpanded] = useState(false);

  const allBrandNames = useMemo(() => {
    return Object.keys(CAR_BRAND_LOGOS).sort((a, b) => a.localeCompare(b));
  }, []);

  const filteredBrands = useMemo(() => {
    let list = allBrandNames;

    if (selectedRegion !== 'All') {
      const allowed = new Set(REGIONAL_GROUPS[selectedRegion] || []);
      list = list.filter((b) => allowed.has(b));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((b) => b.toLowerCase().includes(q));
    }

    return list;
  }, [allBrandNames, selectedRegion, searchQuery]);

  // Initial display limit when collapsed
  const displayLimit = isExpanded ? filteredBrands.length : 24;
  const displayedBrands = filteredBrands.slice(0, displayLimit);

  return (
    <div className="rounded-3xl border border-white/10 bg-[#0A1633] p-5 sm:p-7 space-y-6 shadow-2xl">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-white/10 pb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="flex size-6 items-center justify-center rounded-lg bg-blue-500/20 text-blue-400">
              <Car className="size-3.5" />
            </span>
            <span className="text-xs font-black uppercase tracking-wider text-emerald-400">
              Complete Automotive Taxonomy ({allBrandNames.length} Verified Makes)
            </span>
          </div>
          <h3 className="text-xl sm:text-2xl font-extrabold text-white">
            All Vehicle Makes &amp; Manufacturers
          </h3>
          <p className="text-xs sm:text-sm text-slate-300 mt-1">
            Browse genuine and OEM parts for every automobile registered across {countryName}.
          </p>
        </div>

        {/* Live Filter Input */}
        <div className="relative min-w-[240px] sm:min-w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search all 180+ vehicle makes..."
            className="w-full h-10 pl-9 pr-3 rounded-xl bg-[#060D1E] border border-white/15 text-xs text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-semibold"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 hover:text-white bg-white/10 px-1.5 py-0.5 rounded"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Region Filter Pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none text-xs">
        {['All', ...Object.keys(REGIONAL_GROUPS)].map((region) => (
          <button
            key={region}
            type="button"
            onClick={() => setSelectedRegion(region)}
            className={`px-3 py-1.5 rounded-xl font-bold whitespace-nowrap transition-all ${
              selectedRegion === region
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10'
            }`}
          >
            {region}
          </button>
        ))}
        <span className="text-[11px] text-slate-400 ml-auto pl-2 font-mono shrink-0">
          Showing {filteredBrands.length} makes
        </span>
      </div>

      {/* Brands Grid */}
      {displayedBrands.length === 0 ? (
        <div className="text-center py-10 rounded-2xl bg-white/5 border border-white/10 space-y-2">
          <p className="text-sm font-bold text-slate-200">No vehicle makes matching "{searchQuery}"</p>
          <button
            type="button"
            onClick={() => {
              setSearchQuery('');
              setSelectedRegion('All');
            }}
            className="text-xs text-blue-400 hover:underline font-bold"
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3">
          {displayedBrands.map((brand) => (
            <Link
              key={brand}
              href={`/${countrySlug}/shop?make=${encodeURIComponent(brand)}`}
              className="group flex items-center gap-2.5 p-2.5 sm:p-3 rounded-xl bg-[#060D1E]/80 hover:bg-[#101D42] border border-white/10 hover:border-blue-500/50 transition-all shadow-xs"
            >
              <VehicleBrandLogo
                brand={brand}
                size="sm"
                variant="badge"
                className="shrink-0 group-hover:scale-105 transition-transform"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors truncate">
                  {brand}
                </p>
                <span className="text-[10px] text-slate-400 font-semibold group-hover:text-slate-300">
                  Shop Parts →
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Expand / Collapse Toggle if More than 24 Brands */}
      {filteredBrands.length > 24 && !searchQuery && (
        <div className="pt-2 text-center border-t border-white/10">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/15 text-white text-xs font-extrabold transition-all border border-white/15 cursor-pointer"
          >
            {isExpanded ? (
              <>
                <span>Show Fewer Makes</span>
                <ChevronUp className="size-4" />
              </>
            ) : (
              <>
                <span>View All {filteredBrands.length} Vehicle Makes</span>
                <ChevronDown className="size-4" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
